import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Button, ButtonBase, Checkbox, Collapse, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import {
  DELIVERY_METHODS,
  EMPTY_ORDER_ATTR_FILTER,
  isOrderAttrFilterActive,
  OrderAttrFilter,
  SEGMENT_STATS,
  toggledHiddenValue,
} from './orderAttrFilter';

const sectionLabelSx = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary',
} as const;

interface Props {
  value: OrderAttrFilter;
  onChange: (next: OrderAttrFilter, changed: { label: string; visible: boolean }) => void;
}

/**
 * 「表示項目」内に置く表示対象の絞り込み（配送形態・区間種別）。
 * チェックが付いている = 表示。外した値を持つ案件と、その案件を積む車両が消える。
 */
const OrderAttrFilterSection = ({ value, onChange }: Props) => {
  const [openGroups, setOpenGroups] = useState({ deliveryMethod: true, segmentStat: true });
  const active = isOrderAttrFilterActive(value);

  const checkboxRow = (label: string, hidden: Set<string>, onToggle: (next: Set<string>) => void) => (
    <Stack
      key={label}
      direction="row"
      alignItems="center"
      spacing={1}
      component="label"
      sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Checkbox
        size="small"
        checked={!hidden.has(label)}
        onChange={() => onToggle(toggledHiddenValue(hidden, label))}
        inputProps={{ 'aria-label': label }}
        sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
      />
      <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary' }}>
        {label}
      </Typography>
    </Stack>
  );

  /* カテゴリヘッダは既存の表示情報と同じ ButtonBase（= ネイティブ button）。キーボードで開閉できる */
  const category = (
    label: string,
    id: keyof typeof openGroups,
    options: readonly string[],
    hidden: Set<string>,
    onToggle: (next: Set<string>) => void,
  ) => (
    <Box>
      <ButtonBase
        onClick={() => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }))}
        aria-expanded={openGroups[id]}
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: 0.75, textAlign: 'left', '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="caption" sx={sectionLabelSx}>{label}</Typography>
        {openGroups[id] ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
      </ButtonBase>
      <Collapse in={openGroups[id]}>
        <Stack sx={{ px: 1, pb: 1 }} spacing={0.25}>
          {options.map((option) => checkboxRow(option, hidden, onToggle))}
        </Stack>
      </Collapse>
    </Box>
  );

  return (
    <>
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" sx={sectionLabelSx} display="block">表示対象</Typography>
          <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }} display="block">
            車両・未割当に反映
          </Typography>
        </Box>
        {active && (
          <Button
            size="small"
            onClick={() => onChange(EMPTY_ORDER_ATTR_FILTER, { label: 'すべて', visible: true })}
            sx={{ minWidth: 0, px: 0.5, fontSize: 10, textTransform: 'none' }}
          >
            解除
          </Button>
        )}
      </Box>

      {category('配送形態', 'deliveryMethod', DELIVERY_METHODS, value.hiddenDeliveryMethods, (next) => (
        onChange(
          { ...value, hiddenDeliveryMethods: next },
          { label: '配送形態', visible: next.size < value.hiddenDeliveryMethods.size },
        )
      ))}
      {category('区間種別', 'segmentStat', SEGMENT_STATS, value.hiddenSegmentStats, (next) => (
        onChange(
          { ...value, hiddenSegmentStats: next },
          { label: '区間種別', visible: next.size < value.hiddenSegmentStats.size },
        )
      ))}
    </>
  );
};

export default OrderAttrFilterSection;
